import { createStore, type StoreApi } from 'zustand/vanilla';
import {
  createHistory,
  executeCommand,
  mergeRecent,
  redo as redoStep,
  undo as undoStep,
  type Command,
  type HistoryState,
  type Patch,
  type ProjectDocument,
} from '@indusketch/project-model';
import type { ProjectAccess } from '@indusketch/shared';
import { ApiError } from '../../../lib/api/client.js';
import { userMessage } from '../../../lib/errors.js';
import { currentI18n } from '../../../i18n/i18n.js';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

export interface EditorState {
  projectId: string | null;
  /** What this user may do with the project; viewers only look and walk through it. */
  access: ProjectAccess;
  revision: number;
  document: ProjectDocument | null;
  /** Last document known to be persisted; identity comparison gives cheap dirty tracking. */
  savedDocument: ProjectDocument | null;
  history: HistoryState;
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  message: string | null;
  /**
   * Live result of a 3D drag, shown by the scene only. Never saved and never in the history;
   * the drag ends with a regular command on `document`.
   */
  preview: ProjectDocument | null;
  setPreview: (doc: ProjectDocument | null) => void;

  /**
   * Set while working live with others: every change goes to the server instead of being
   * saved here, and changes of others arrive through `applyRemote`.
   */
  live: LiveLink | null;
  setLive: (link: LiveLink | null) => void;
  /** Nothing can be changed (a game is being played); the document stays as it is. */
  locked: boolean;
  setLocked: (locked: boolean) => void;
  /** The view after changes of others; the own undo history stays as it is. */
  applyRemote: (document: ProjectDocument) => void;

  load: (
    projectId: string,
    revision: number,
    document: ProjectDocument,
    access?: ProjectAccess,
  ) => void;
  execute: (command: Command) => boolean;
  /**
   * Validated change that is persisted but not recorded in the undo history. Reserved for
   * pure view preferences (roof visibility, transparency) so toggling them does not bury
   * real planning steps in the history. Patch-based undo stays consistent because recorded
   * patches never touch these paths.
   */
  executeUntracked: (command: Command) => boolean;
  /**
   * View preference (roof, transparent walls, grid): like `executeUntracked` for editors; for
   * viewers it only changes the own view, is neither saved nor shared and counts as saved.
   */
  executeView: (command: Command) => boolean;
  /**
   * Starts a run of changes that ends up as one undo step (e.g. holding a rotate button);
   * call the returned function when the run is over.
   */
  batch: () => () => void;
  undo: () => void;
  redo: () => void;
  save: () => Promise<void>;
  clearMessage: () => void;
}

export interface LiveLink {
  submit(patches: readonly Patch[]): void;
}

export type SaveFn = (
  projectId: string,
  baseRevision: number,
  doc: ProjectDocument,
) => Promise<{ revision: number }>;

export function createEditorStore(saveFn: SaveFn): StoreApi<EditorState> {
  return createStore<EditorState>()((set, get) => {
    /** Live: the change goes to the others; it counts as saved here, the server keeps it. */
    const share = (patches: readonly Patch[] | undefined) => {
      const { live, document } = get();
      if (!live || !patches || patches.length === 0) return;
      set({ savedDocument: document });
      live.submit(patches);
    };
    /** Changes of others may have removed what an own step refers to. */
    const stepSafely = <T>(step: () => T): T | null => {
      try {
        return step();
      } catch {
        set({ message: currentI18n().m.live.resynced });
        return null;
      }
    };
    return {
      projectId: null,
      access: 'owner',
      revision: 0,
      document: null,
      savedDocument: null,
      history: createHistory(),
      saveStatus: 'idle',
      lastSavedAt: null,
      message: null,
      preview: null,
      live: null,
      locked: false,

      setPreview(preview) {
        set({ preview });
      },

      setLive(live) {
        set({ live });
      },

      setLocked(locked) {
        set({ locked, preview: null });
      },

      applyRemote(document) {
        set({ document, savedDocument: document });
      },

      load(projectId, revision, document, access) {
        set({
          projectId,
          access: access ?? get().access,
          revision,
          document,
          savedDocument: document,
          history: createHistory(),
          saveStatus: 'idle',
          message: null,
          preview: null,
        });
      },

      execute(command) {
        const { document, history } = get();
        if (!document || get().locked) return false;
        if (get().access === 'viewer') {
          set({ message: currentI18n().m.share.readOnly });
          return false;
        }
        let result;
        try {
          result = executeCommand(document, history, command);
        } catch (err) {
          // A failing command must never take the editor down; the document stays unchanged.
          console.error(err);
          const { m, fmt } = currentI18n();
          set({ message: fmt(m.editor.messages.commandFailed, { label: command.label }) });
          return false;
        }
        if (!result.ok) {
          const first = result.issues[0];
          const { m, fmt } = currentI18n();
          set({
            message: first
              ? fmt(m.editor.messages.commandRejectedWhy, {
                  label: command.label,
                  reason: first.message,
                })
              : fmt(m.editor.messages.commandRejected, { label: command.label }),
          });
          return false;
        }
        if (result.changed) {
          set({ document: result.document, history: result.history, message: null });
          share(result.history.past.at(-1)?.patches);
        }
        return true;
      },

      executeUntracked(command) {
        const { document, history } = get();
        if (!document || get().access === 'viewer' || get().locked) return false;
        const result = executeCommand(document, history, command);
        if (!result.ok) return false;
        if (result.changed) {
          set({ document: result.document });
          share(result.history.past.at(-1)?.patches);
        }
        return true;
      },

      executeView(command) {
        const { document, history, access } = get();
        if (access !== 'viewer') return get().executeUntracked(command);
        if (!document) return false;
        const result = executeCommand(document, history, command);
        if (!result.ok) return false;
        if (result.changed) set({ document: result.document, savedDocument: result.document });
        return true;
      },

      batch() {
        const since = get().history.past.at(-1) ?? null;
        return () => set({ history: mergeRecent(get().history, since) });
      },

      undo() {
        const { document, history } = get();
        const entry = history.past.at(-1);
        if (!document || !entry || get().locked) return;
        const step = stepSafely(() => undoStep(document, history));
        if (!step) return;
        set({ document: step.document, history: step.history });
        share(entry.inversePatches);
      },

      redo() {
        const { document, history } = get();
        const entry = history.future[0];
        if (!document || !entry || get().locked) return;
        const step = stepSafely(() => redoStep(document, history));
        if (!step) return;
        set({ document: step.document, history: step.history });
        share(entry.patches);
      },

      async save() {
        const { projectId, document, revision, saveStatus, access, live } = get();
        // Live, the server stores the room's state itself.
        if (live) return;
        if (!projectId || !document || saveStatus === 'saving' || access === 'viewer') return;
        // Nothing new to send (e.g. autosave already ran): just confirm the saved state.
        if (document === get().savedDocument && saveStatus !== 'conflict') {
          set({ saveStatus: 'saved', lastSavedAt: get().lastSavedAt ?? new Date() });
          return;
        }
        set({ saveStatus: 'saving' });
        try {
          const result = await saveFn(projectId, revision, document);
          // `document` is the state that was sent; edits made meanwhile stay dirty.
          set({
            revision: result.revision,
            savedDocument: document,
            saveStatus: 'saved',
            lastSavedAt: new Date(),
            message: null,
          });
        } catch (err) {
          if (err instanceof ApiError && err.code === 'CONFLICT') {
            set({
              saveStatus: 'conflict',
              message: currentI18n().m.editor.messages.saveConflict,
            });
          } else {
            set({ saveStatus: 'error', message: userMessage(err) });
          }
        }
      },

      clearMessage() {
        set({ message: null });
      },
    };
  });
}

export const isDirty = (s: EditorState): boolean => s.document !== s.savedDocument;
export const canUndo = (s: EditorState): boolean => s.history.past.length > 0;
export const canRedo = (s: EditorState): boolean => s.history.future.length > 0;
